<template>
    <div class="icon-picker-root">
        <!-- Segmented control -->
        <div class="seg-control" role="tablist">
            <button
                type="button"
                class="seg-btn"
                :class="{ active: mode === 'preset' }"
                @click="mode = 'preset'"
            >
                <i class="bi bi-grid-3x3-gap"></i>
                {{ $t('spheres.iconPicker.tabs.preset') }}
            </button>
            <button
                type="button"
                class="seg-btn"
                :class="{ active: mode === 'upload' }"
                @click="mode = 'upload'"
            >
                <i class="bi bi-cloud-arrow-up"></i>
                {{ $t('spheres.iconPicker.tabs.upload') }}
            </button>
        </div>

        <!-- Preset icons -->
        <div v-if="mode === 'preset'" class="preset-grid">
            <button
                v-for="slug in icons"
                :key="slug"
                type="button"
                class="preset-tile"
                :class="{ 'preset-tile-active': modelValue.icon_slug === slug }"
                @click="selectPreset(slug)"
                :title="slug"
            >
                <img :src="presetSrc(slug)" :alt="slug" />
                <span class="preset-tile-label">{{ slug }}</span>
            </button>
        </div>

        <!-- Upload -->
        <div v-else class="upload-zone">
            <div v-if="modelValue.preview_url || modelValue.icon_url" class="upload-preview">
                <div class="upload-preview-thumb">
                    <img
                        v-if="modelValue.preview_url"
                        :src="modelValue.preview_url"
                        alt="upload preview"
                    />
                    <i v-else class="bi bi-image"></i>
                </div>
                <div class="upload-preview-info">
                    <div class="upload-preview-title">
                        <i class="bi bi-check-circle-fill"></i>
                        {{ $t('spheres.iconPicker.uploadPreview.title') }}
                    </div>
                    <div class="upload-preview-meta">{{ $t('spheres.iconPicker.uploadPreview.meta') }}</div>
                </div>
                <button
                    type="button"
                    class="upload-remove"
                    @click="removeUpload"
                    :disabled="uploading"
                    :title="$t('spheres.iconPicker.uploadPreview.removeTitle')"
                >
                    <i class="bi bi-trash"></i>
                </button>
            </div>

            <label v-else class="upload-drop" :class="{ 'upload-drop-busy': uploading }">
                <input
                    ref="fileInput"
                    type="file"
                    accept="image/svg+xml,image/png,image/jpeg"
                    :disabled="uploading"
                    @change="onFileChange"
                />
                <div class="upload-drop-icon">
                    <i v-if="uploading" class="bi bi-arrow-repeat upload-spin"></i>
                    <i v-else class="bi bi-cloud-arrow-up"></i>
                </div>
                <div class="upload-drop-title">
                    {{ uploading ? $t('spheres.iconPicker.uploadDrop.uploading') : $t('spheres.iconPicker.uploadDrop.prompt') }}
                </div>
                <div class="upload-drop-meta">{{ $t('spheres.iconPicker.uploadDrop.meta') }}</div>
            </label>
        </div>
    </div>
</template>

<script>
// v-model shape: { icon_slug: string|null, icon_url: string|null, preview_url: string|null }
// - icon_slug: bundled icon name (e.g. "restaurant"), or null
// - icon_url: raw S3 key returned by /upload-icon (e.g. "sphere-icons/<uuid>.svg"), or null
// - preview_url: presigned URL for displaying a custom upload locally (transient)
// Choosing one mode resets the other so the parent never has both set.

export default {
    name: 'IconPicker',
    props: {
        modelValue: {
            type: Object,
            default: () => ({ icon_slug: null, icon_url: null, preview_url: null })
        }
    },
    emits: ['update:modelValue'],
    data() {
        return {
            uploading: false,
            mode: this.modelValue && (this.modelValue.icon_url || (this.modelValue.preview_url && !this.modelValue.icon_slug)) ? 'upload' : 'preset',
            icons: [
                'restaurant',
                'retail',
                'services',
                'beauty',
                'healthcare',
                'education',
                'tech',
                'real_estate',
                'entertainment',
                'finance',
                'transport',
                'other'
            ]
        }
    },
    methods: {
        presetSrc(slug) {
            return '/icons/spheres/' + slug + '.svg'
        },
        selectPreset(slug) {
            this.$emit('update:modelValue', { icon_slug: slug, icon_url: null, preview_url: null })
        },
        removeUpload() {
            this.$emit('update:modelValue', { icon_slug: null, icon_url: null, preview_url: null })
            if (this.$refs.fileInput) this.$refs.fileInput.value = ''
        },
        async onFileChange(e) {
            const file = e.target.files && e.target.files[0]
            if (!file) return
            if (file.size > 256 * 1024) {
                this.$notify({ text: this.$t('spheres.iconPicker.notify.tooLarge'), type: 'error' })
                e.target.value = ''
                return
            }
            this.uploading = true
            try {
                const fd = new FormData()
                fd.append('icon', file)
                const res = await fetch(process.env.VUE_APP_API_URL + '/spheres/upload-icon', {
                    method: 'POST',
                    credentials: 'include',
                    body: fd
                })
                const json = await res.json()
                if (json.result === 'SUCCESS') {
                    this.$emit('update:modelValue', {
                        icon_slug: null,
                        icon_url: json.data.icon_url,
                        preview_url: json.data.preview_url || null
                    })
                    this.$notify({ text: this.$t('spheres.iconPicker.notify.uploaded'), type: 'success' })
                } else {
                    this.$notify({ text: json.msg || this.$t('spheres.iconPicker.notify.uploadFailed'), type: 'error' })
                }
            } catch (err) {
                this.$notify({ text: err.message || this.$t('spheres.iconPicker.notify.uploadError'), type: 'error' })
            } finally {
                this.uploading = false
                if (this.$refs.fileInput) this.$refs.fileInput.value = ''
            }
        }
    }
}
</script>

<style scoped>
.icon-picker-root {
    width: 100%;
}

/* Segmented control */
.seg-control {
    display: inline-flex;
    background: #f1f5f9;
    border-radius: 10px;
    padding: 4px;
    gap: 2px;
    margin-bottom: 14px;
}
.seg-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    background: transparent;
    border: none;
    border-radius: 7px;
    font-size: 13px;
    font-weight: 500;
    color: #64748b;
    cursor: pointer;
    transition: all 0.15s ease;
}
.seg-btn i {
    font-size: 13px;
}
.seg-btn:hover {
    color: #475569;
}
.seg-btn.active {
    background: #fff;
    color: #7c3aed;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
}
.seg-btn.active i {
    color: #7c3aed;
}

/* Preset grid */
.preset-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
    gap: 8px;
}
.preset-tile {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 12px 8px;
    background: #fff;
    border: 1.5px solid #e5e7eb;
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.12s ease;
    text-align: center;
}
.preset-tile:hover {
    border-color: #c4b5fd;
    background: #faf7ff;
}
.preset-tile-active {
    border-color: #7c3aed;
    background: #f5f0ff;
    box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.15);
}
.preset-tile img {
    width: 32px;
    height: 32px;
    object-fit: contain;
}
.preset-tile-label {
    font-size: 11px;
    font-family: ui-monospace, monospace;
    color: #94a3b8;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
}
.preset-tile-active .preset-tile-label {
    color: #6d28d9;
}

/* Upload */
.upload-zone {
    width: 100%;
}
.upload-drop {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 28px 16px;
    background: #fafbfc;
    border: 2px dashed #cbd5e1;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.15s ease;
    text-align: center;
    margin: 0;
}
.upload-drop:hover {
    border-color: #7c3aed;
    background: #faf7ff;
}
.upload-drop-busy {
    cursor: wait;
    border-color: #7c3aed;
    background: #faf7ff;
}
.upload-drop input[type="file"] {
    display: none;
}
.upload-drop-icon {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: #f5f0ff;
    color: #7c3aed;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    margin-bottom: 10px;
}
.upload-drop-title {
    font-size: 14px;
    font-weight: 500;
    color: #1e293b;
    margin-bottom: 4px;
}
.upload-drop-meta {
    font-size: 12px;
    color: #94a3b8;
}
.upload-spin {
    animation: upload-spin 1s linear infinite;
}
@keyframes upload-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}

.upload-preview {
    display: flex;
    align-items: center;
    gap: 12px;
    background: #faf7ff;
    border: 1px solid #ede9fe;
    border-radius: 12px;
    padding: 12px 14px;
}
.upload-preview-thumb {
    width: 48px;
    height: 48px;
    border-radius: 8px;
    background: #fff;
    border: 1px solid #e5e7eb;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    overflow: hidden;
}
.upload-preview-thumb img {
    width: 36px;
    height: 36px;
    object-fit: contain;
}
.upload-preview-thumb i {
    font-size: 22px;
    color: #cbd5e1;
}
.upload-preview-info {
    flex: 1;
    min-width: 0;
}
.upload-preview-title {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: #15803d;
    margin-bottom: 2px;
}
.upload-preview-title i {
    color: #22c55e;
}
.upload-preview-meta {
    font-size: 12px;
    color: #94a3b8;
}
.upload-remove {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: 1px solid #fecaca;
    background: #fff;
    color: #dc2626;
    cursor: pointer;
    transition: all 0.15s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}
.upload-remove:hover:not(:disabled) {
    background: #fef2f2;
    border-color: #dc2626;
}
.upload-remove:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}
</style>
